import { useState, useMemo, type ReactNode } from 'react';
import { t } from '@/i18n';

interface FormulaFieldFilterItem {
  label: string;
  formula: string;
}

interface FormulaFieldFilterProps<T extends FormulaFieldFilterItem> {
  items: T[];
  renderItem: (item: T & { index: number }) => ReactNode;
  placeholder?: string;
  emptyMessage?: string;
}

export function FormulaFieldFilter<T extends FormulaFieldFilterItem>({
  items,
  renderItem,
  placeholder,
  emptyMessage,
}: FormulaFieldFilterProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const tokens = searchQuery
      .toLowerCase()
      .split(' ')
      .map((t) => t.trim())
      .filter(Boolean);
    return items.filter((item) => {
      const searchText = `${item.label || ''} ${item.formula ?? ''}`.toLowerCase();
      return tokens.some((token) => searchText.includes(token));
    });
  }, [items, searchQuery]);

  if (items.length <= 2) {
    return <>{items.map((item, index) => <div key={index}>{renderItem({ ...item, index })}</div>)}</>;
  }

  return (
    <div data-testid="formula-filter-container">
      <div className="position-relative mb-2">
        <input
          className="form-control form-control-sm"
          type="text"
          placeholder={placeholder ?? t('dashboard.formulaFieldFilterPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="formula-filter-input"
        />
        {searchQuery && (
          <button
            type="button"
            className="btn btn-link btn-sm position-absolute end-0 top-50 translate-middle-y"
            onClick={() => setSearchQuery('')}
            data-testid="formula-filter-reset"
            aria-label={t('dashboard.formulaFieldFilterClear')}
          >
            <i className="las la-times" />
          </button>
        )}
      </div>
      {filteredItems.length === 0 ? (
        <div className="text-muted small">
          {emptyMessage ?? t('dashboard.formulaFieldFilterNoResults')}
        </div>
      ) : (
        filteredItems.map((item, index) => (
          <div key={index}>{renderItem({ ...item, index })}</div>
        ))
      )}
    </div>
  );
}
